import { extendTheme } from "@chakra-ui/react";
import { mode } from "@chakra-ui/theme-tools";

import wamColors from "./colors";
import buttonTheme from "./components/button";
import cardTheme from "./components/card";
import badgeTheme from "./components/badge";

const config = {
  initialColorMode: "light",
  useSystemColorMode: false,
};

const styles = {
  global: (props) => ({
    ":root": {
      "--wam-color-app": wamColors.surface.app,
      "--wam-color-card": wamColors.surface.card,
      "--wam-color-border": wamColors.surface.border,
      "--wam-color-text": wamColors.text.primary,
      "--wam-color-muted": wamColors.text.secondary,
    },
    "html, body": {
      minHeight: "100%",
      backgroundColor: mode(wamColors.surface.app, wamColors.surface.darkApp)(props),
      color: mode(wamColors.text.primary, wamColors.text.inverted)(props),
      backgroundImage: mode(
        "radial-gradient(circle at top left, rgba(37, 99, 235, 0.08), transparent 28%), radial-gradient(circle at top right, rgba(6, 182, 212, 0.08), transparent 24%)",
        "radial-gradient(circle at top left, rgba(37, 99, 235, 0.16), transparent 26%), radial-gradient(circle at top right, rgba(6, 182, 212, 0.12), transparent 20%)"
      )(props),
    },
    body: {
      overflowX: "hidden",
    },
    "::selection": {
      backgroundColor: mode("rgba(37, 99, 235, 0.28)", "rgba(96, 165, 250, 0.35)")(props),
    },
  }),
};

const semanticTokens = {
  colors: {
    "wam.app.bg": {
      default: wamColors.surface.app,
      _dark: wamColors.surface.darkApp,
    },
    "wam.card.bg": {
      default: wamColors.surface.card,
      _dark: wamColors.surface.darkCard,
    },
    "wam.border": {
      default: wamColors.surface.border,
      _dark: wamColors.surface.darkBorder,
    },
    "wam.text.primary": {
      default: wamColors.text.primary,
      _dark: wamColors.text.inverted,
    },
    "wam.text.secondary": {
      default: wamColors.text.secondary,
      _dark: wamColors.neutral[300],
    },
  },
};

const components = {
  Button: buttonTheme,
  Card: cardTheme,
  Badge: badgeTheme,
  Drawer: {
    baseStyle: (props) => ({
      dialog: {
        bg: mode("white", wamColors.surface.darkCard)(props),
        color: mode("gray.800", "gray.100")(props),
      },
    }),
  },
  Modal: {
    baseStyle: (props) => ({
      dialog: {
        bg: mode("white", wamColors.surface.darkCard)(props),
        color: mode("gray.800", "gray.100")(props),
      },
    }),
  },
  Input: {
    variants: {
      outline: (props) => ({
        field: {
          borderRadius: "18px",
          borderColor: mode("neutral.200", "whiteAlpha.200")(props),
          bg: mode("white", "whiteAlpha.100")(props),
          _hover: {
            borderColor: mode("brand.300", "accent.300")(props),
          },
          _focusVisible: {
            borderColor: mode("brand.500", "accent.300")(props),
            boxShadow: mode("0 0 0 1px #2563EB", "0 0 0 1px #67E8F9")(props),
          },
        },
      }),
    },
    defaultProps: {
      variant: "outline",
    },
  },
  Select: {
    variants: {
      outline: (props) => ({
        field: {
          borderRadius: "18px",
          borderColor: mode("neutral.200", "whiteAlpha.200")(props),
          bg: mode("white", "whiteAlpha.100")(props),
          _hover: {
            borderColor: mode("brand.300", "accent.300")(props),
          },
          _focusVisible: {
            borderColor: mode("brand.500", "accent.300")(props),
            boxShadow: mode("0 0 0 1px #2563EB", "0 0 0 1px #67E8F9")(props),
          },
        },
      }),
    },
    defaultProps: {
      variant: "outline",
    },
  },
  Textarea: {
    variants: {
      outline: (props) => ({
        borderRadius: "22px",
        borderColor: mode("neutral.200", "whiteAlpha.200")(props),
        bg: mode("white", "whiteAlpha.100")(props),
        _hover: {
          borderColor: mode("brand.300", "accent.300")(props),
        },
        _focusVisible: {
          borderColor: mode("brand.500", "accent.300")(props),
          boxShadow: mode("0 0 0 1px #2563EB", "0 0 0 1px #67E8F9")(props),
        },
      }),
    },
    defaultProps: {
      variant: "outline",
    },
  },
  Menu: {
    /*
     * zIndex del popover del menú.
     *
     * El baseStyle de Chakra v1.8.9 define `list.zIndex: 1`, y MenuList usa ese valor
     * para el div posicionador del popper. La regla global de _NavigationApp que vuelve
     * sticky la última columna de toda `table.chakra-table` le pone `zIndex: 2` a ese
     * `td:last-child` (y 3 al `th`), así que el menú de la columna ACCIONES quedaba
     * pintado POR DEBAJO de las celdas sticky: se veía cortado en vertical, incluso con
     * <Portal>, porque el portal lo saca del <td> pero lo deja en z-index 1.
     *
     * `popover` (1500) lo deja por encima de la columna sticky y por debajo de
     * skipLink/toast/tooltip.
     *
     * Solo se pisa zIndex: `bg` y `boxShadow` del baseStyle de Chakra ya son mode-aware
     * (`mode("#fff","gray.700")` / `mode("sm","dark-lg")`), así que no se tocan.
     */
    baseStyle: {
      list: {
        zIndex: "popover",
      },
    },
  },
  Table: {
    baseStyle: (props) => ({
      th: {
        bg: mode("neutral.50", "whiteAlpha.100")(props),
        color: mode("neutral.700", "neutral.100")(props),
      },
      td: {
        borderColor: mode("neutral.200", "whiteAlpha.200")(props),
      },
    }),
  },
};

const theme = extendTheme({
  config,
  colors: wamColors,
  semanticTokens,
  styles,
  components,
  fonts: {
    heading: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
  },
});

export default theme;
